import Modal from "@/components/UI/Modal/Modal";
import {timeString} from "@/utils/time";
import {useCrosswordState} from "@/pages/crossword";
import {CrosswordDataType, crosswordDate} from "@/types/crosswords";
import Confetti from "@/components/Spellify/EndMessages/Confetti/Confetti";
import {useEffect, useMemo, useState} from "react";
import styles from "@/components/Spellify/EndMessages/EndMessages.module.scss";
import Button from "@/components/UI/Button/Button";
import Icon from "@/components/UI/Icon/Icon";
import Countdown from "@/components/Spellify/EndMessages/Countdown/Countdown";
import Message from "@/components/UI/Message/Message";
import {useCookies} from "react-cookie";
import {useRouter} from "next/router";

const iosPlatforms = ['iPhone', 'iPad', 'iPod']

type Props = {
  open: boolean;
  onClose: () => void;
  crossword: CrosswordDataType
}

const EndModal = ({open, onClose, crossword}: Props) => {
  const [fastestTime] = useCrosswordState('crosswordFastestTime')
  const [hasCheated] = useCrosswordState('crosswordHasCheated')
  const [timeElapsed] = useCrosswordState('crosswordTimeElapsed')
  const [crosswordTimes] = useCrosswordState('crosswordTimes')
  const [copied, setCopied] = useState<string>("")
  const [isShareable, setIsShareable] = useState(false)
  const router = useRouter()
  const [cookies] = useCookies(['displayName'])
  const displayName = cookies.displayName
  const redirect = router.basePath + router.asPath

  const messageString = useMemo(() => {
    let message = 'EDHREC Crossword\n'
    message += crosswordDate(crossword.date)?.toLocaleDateString()
    message += '\n\n'
    message += `Words: ${crossword.downAnswers.length + crossword.acrossAnswers.length}\n`
    message += `My Time: ${timeString(timeElapsed)}\n`
    return message
  }, [timeElapsed, hasCheated, crossword])

  const writeToClipboard = () => {
    navigator.clipboard.writeText(`${messageString}\nhttps://edhrec.com/games/crossword`)
    setCopied("Copied!")
    setTimeout(() => setCopied(""), 1000)
  }

  useEffect(() => {
    setIsShareable(navigator.userAgent.indexOf('Firefox') === -1 && !!navigator.share && (iosPlatforms.includes(navigator.platform) || /Android/.test(navigator.userAgent)))
  }, []);


  return (
    <Modal open={open} onClose={onClose} closeIcon>
      <Confetti gameState="success" alwaysFire/>
      <h3>Congratulations!</h3>
      <p>
        You completed the puzzle in <b>{timeString(timeElapsed)}</b>!
        <br/>
        <i>-with {hasCheated ? 'some hints.' : 'no hints!'}</i>
      </p>
      <div className={styles.shareButtonContainer}>
        <a
          href={`https://bsky.app/intent/compose?text=${encodeURIComponent(messageString+'\nhttps://edhrec.com/games/crossword')}`}
          target="_blank"
          rel="noreferrer"
          className={styles.twitterLink}
        >
          <Button className={styles.blueskyButton}><Icon icon="bluesky"/> Bluesky</Button>
        </a>
        <a
          href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(messageString)}&url=https://edhrec.com/games/crossword`}
          target="_blank"
          rel="noreferrer"
          className={styles.twitterLink}
        >
          <Button className={styles.twitterButton}><Icon icon="twitter"/> Twitter</Button>
        </a>
        {isShareable && (
          <Button className={`${styles.shareButton}`} type="button" onClick={() => {navigator.share({title: "Spellify", text: messageString, url: `https://edhrec.com/games/crossword`})}}><Icon icon="share"/> Share</Button>
        )}
        {!isShareable && (
          <Button className={`${styles.shareButton}`} type="button" onClick={() => {writeToClipboard()}}><Icon icon="clipboard"/> {copied ? copied :  'Copy to clipboard'}</Button>
        )}
      </div>

      <Countdown title="Crossword" targetDate={crosswordDate(crossword.nextDate)} />

      {!displayName &&
        <Message type="warning">
          <p>
            Your stats are saved in your browser's local storage. If you want permanent saving across
            devices, login or sign up for a free EDHREC account.
          </p>
          <div className={styles.buttonGroup}>
            <a href={`/signin?utm_source=games&utm_medium=CrosswordEnd&redirect=${redirect}`}><Button color="grey">Sign In</Button></a>
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
            <a href={`/signup?utm_source=games&utm_medium=CrosswordEnd&redirect=${redirect}`}><Button color="blue">Sign Up</Button></a>
          </div>

        </Message>
      }

      <h3>Your Stats:</h3>
      <ul>
        <li>
          Fastest: {timeString(fastestTime || 0)}
          {fastestTime === timeElapsed && <b> (New Record!)</b>}
        </li>
        <li>
          All Time Completed: {Object.keys(crosswordTimes).length}
        </li>
      </ul>
    </Modal>
  )
}

export default EndModal